import { FC } from "react";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Modal,
  Typography,
  SxProps,
  Theme,
} from "@mui/material";
import CloseSvg from "@/assets/svg/CloseSvg";
import BasketSvg from "@/assets/svg/BasketSvg";
import { styles } from "./stylesDeleteModal";

interface IDeleteModalProps {
  open: boolean;
  handleClose: () => void;
  onDelete: () => void;

  title?: string;
  description?: string;
  variant?: "post" | "comment" | string;

  width?: string | number;
  height?: string | number;
  iconSize?: number;
  iconInnerSize?: number;
  sxWrapper?: SxProps<Theme>;
  sxIconWrapper?: SxProps<Theme>;
  sxTitle?: SxProps<Theme>;
  sxDescription?: SxProps<Theme>;
  sxButtonWrapper?: SxProps<Theme>;
  sxCancelButton?: SxProps<Theme>;
  sxDeleteButton?: SxProps<Theme>;
  sxCloseBtn?: SxProps<Theme>;
}

const DeleteModal: FC<IDeleteModalProps> = ({
  open,
  handleClose,
  onDelete,
  variant = "post",
  title,
  description,
  width,
  height,
  iconSize = 48,
  iconInnerSize = 24,
  sxWrapper,
  sxIconWrapper,
  sxTitle,
  sxDescription,
  sxButtonWrapper,
  sxCancelButton,
  sxDeleteButton,
  sxCloseBtn,
}) => {
  const onConfirm = () => {
    handleClose();
    onDelete();
  };

  const finalTitle =
    title ?? `Are you sure you want to delete ${variant === "post" ? "the" : "this"} ${variant}?`;
  const defaultDescription = variant === "post" ? "You won't be able to recover it" : null;
  const finalDescription = description !== undefined ? description : defaultDescription;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          ...styles.modal({ width, height }),
          ...(sxWrapper as any),
        }}
      >
        <IconButton
          onClick={handleClose}
          size="medium"
          aria-label="close"
          sx={{
            ...styles.closeBtn,
            ...(sxCloseBtn as any),
          }}
        >
          <CloseSvg />
        </IconButton>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          {variant !== "comment" && (
            <Box
              sx={{
                ...styles.basketSvg(iconSize),
                ...(sxIconWrapper as any),
              }}
            >
              <BasketSvg variant="large" width={iconInnerSize} height={iconInnerSize} />
            </Box>
          )}

          <DialogTitle
            sx={{
              ...styles.title(variant === "post"),
              ...(sxTitle as any),
            }}
          >
            {finalTitle}
          </DialogTitle>

          {finalDescription && (
            <DialogContent
              sx={{
                textAlign: "center",
                padding: "0",
                width: "100%",
              }}
            >
              <Typography
                component={"p"}
                sx={{
                  ...styles.description,
                  ...(sxDescription as any),
                }}
              >
                {finalDescription}
              </Typography>
            </DialogContent>
          )}
        </Box>

        <DialogActions
          sx={{
            justifyContent: "center",
            margin: "0",
            padding: "0",
            width: "100%",
          }}
        >
          <Box
            sx={{
              ...styles.buttonWrapper,
              ...(sxButtonWrapper as any),
            }}
          >
            <Button
              onClick={handleClose}
              sx={{ ...styles.cancelButton, ...(sxCancelButton as any) }}
            >
              {variant === "comment" ? "Continue" : "Cancel"}
            </Button>
            <Button onClick={onConfirm} sx={{ ...styles.deleteButton, ...(sxDeleteButton as any) }}>
              Delete
            </Button>
          </Box>
        </DialogActions>
      </Box>
    </Modal>
  );
};

export default DeleteModal;
